import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  searchVocabularyTerms,
  type VocabTerm,
  type VocabularyDisplayDto,
} from '@/api/vocabularies';
import { termSubtitle, termTitle } from '@/components/vocabulary/term-display';
import {
  TermInfoInline,
  TermInfoToggle,
} from '@/components/vocabulary/TermInfoInline';
import { cn } from '@/lib/utils';

import { TermAddAction } from './TermAddAction';

const SEARCH_FETCH_SIZE = 50;

interface SearchResultsListProps {
  readonly query: string;
  readonly display: VocabularyDisplayDto | null;
  readonly excludedHpoIds: ReadonlySet<string>;
  readonly onAddTerm: (term: { id: string; label: string }) => void;
}

/**
 * Flat list of HPO terms matching the search box. Used in place of the
 * `HpoTree` whenever the user has typed two or more characters — since
 * matching arbitrary terms back into the ancestry tree would require
 * per-path lookups the vocabulary service doesn't expose.
 */
export function SearchResultsList({
  query,
  display,
  excludedHpoIds,
  onAddTerm,
}: SearchResultsListProps): ReactElement {
  const { t } = useTranslation();
  const search = useQuery({
    queryKey: ['vocabulary', 'hpo', 'browse-search', query],
    queryFn: ({ signal }) =>
      searchVocabularyTerms('hpo', query, { signal, size: SEARCH_FETCH_SIZE }),
    staleTime: 30_000,
  });
  const results = useMemo(() => search.data ?? [], [search.data]);

  if (search.isError) {
    return (
      <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-3 text-xs text-destructive">
        {t('vocab.picker.error')}
      </p>
    );
  }
  if (search.isPending) {
    return (
      <p className="flex items-center justify-center gap-2 rounded-md border border-border bg-card px-3 py-6 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {t('patient.phenotypes.browse.loading')}
      </p>
    );
  }
  if (results.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-6 text-center text-xs text-muted-foreground">
        {t('patient.phenotypes.browse.noResults')}
      </p>
    );
  }

  return (
    <ul className="max-h-[60vh] overflow-y-auto rounded-md border border-border divide-y divide-border/60">
      {results.map((term) => (
        <SearchResultRow
          key={term.id}
          term={term}
          display={display}
          excluded={excludedHpoIds.has(term.id)}
          onAddTerm={onAddTerm}
        />
      ))}
    </ul>
  );
}

interface SearchResultRowProps {
  readonly term: VocabTerm;
  readonly display: VocabularyDisplayDto | null;
  readonly excluded: boolean;
  readonly onAddTerm: (term: { id: string; label: string }) => void;
}

/** One flat search-result row with a disclosable inline info panel. */
function SearchResultRow({
  term,
  display,
  excluded,
  onAddTerm,
}: SearchResultRowProps): ReactElement {
  const [infoOpen, setInfoOpen] = useState(false);
  const title = termTitle(term, display);
  const subtitle = termSubtitle(term, display) ?? term.synonyms?.join(', ') ?? null;
  return (
    <li className={cn('px-3 py-2', excluded && 'bg-muted/30')}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-[13px] font-medium truncate',
              excluded ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {title}
          </p>
          <p className="text-[11px] text-muted-foreground truncate">
            <span className="font-mono">{term.id}</span>
            {subtitle !== null && subtitle !== '' ? ` · ${subtitle}` : null}
          </p>
        </div>
        <TermInfoToggle
          open={infoOpen}
          onToggle={() => {
            setInfoOpen((v) => !v);
          }}
          termLabel={title}
        />
        <TermAddAction
          termId={term.id}
          label={title}
          excluded={excluded}
          onAddTerm={onAddTerm}
        />
      </div>
      {infoOpen && (
        <TermInfoInline vocabularyId="hpo" termId={term.id} className="mt-2" />
      )}
    </li>
  );
}
